import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { MatSelectInfiniteScrollDirective } from '@shared/ui';

export interface DeviceSelectorItem {
  id: string;
  name: string;
  serialNumber?: string;
  deviceType?: { id?: string; name?: string; version?: string };
}

@Component({
    selector: 'ts-device-selector',
    templateUrl: './device-selector.component.html',
    styleUrls: ['./device-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatProgressSpinnerModule,
        MatSelectInfiniteScrollDirective,
    ]
})
export class DeviceSelectorComponent implements ControlValueAccessor {
  @Input() options: DeviceSelectorItem[] = [];
  @Input() loaded = true;
  @Input() hasMore = false;
  @Optional() @Input() label?: string;
  @Optional() @Input() noItemsMessage?: string;
  @Optional() @Input() placeholder = '';
  @Optional() @Input() dataCy = 'select-device';

  @Output() lastItemReached = new EventEmitter<void>();

  public selected: DeviceSelectorItem | null = null;
  public disabled = false;

  private onChange: (value: DeviceSelectorItem | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(@Self() @Optional() public readonly ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public get isRequired(): boolean {
    return this.ngControl?.control?.hasValidator ? this.hasRequiredValidator() : false;
  }

  public get hasRequiredError(): boolean {
    const control = this.ngControl?.control;
    return !!control && control.hasError('required') && (control.dirty || control.touched);
  }

  public writeValue(value: DeviceSelectorItem | null): void {
    this.selected = value ?? null;
  }

  public registerOnChange(fn: (value: DeviceSelectorItem | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onSelectionChange(value: DeviceSelectorItem): void {
    this.selected = value;
    this.onChange(value);
    this.onTouched();
  }

  public sameId(a?: DeviceSelectorItem, b?: DeviceSelectorItem): boolean {
    return a?.id === b?.id;
  }

  private hasRequiredValidator(): boolean {
    const control = this.ngControl?.control;
    if (!control) return false;
    const validator = control.validator;
    if (!validator) return false;
    const result = validator({} as never);
    return !!(result && (result as { required?: boolean }).required);
  }
}
